import { RulesLogic } from 'json-logic-js';
import { cloneDeep } from 'lodash/fp';
import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { sortModules } from 'dashboard/apps/DashboardLayoutEditor/gridPositioningUtils';
import { useModuleLayout } from 'dashboard/components/GridLayoutRender/ModuleLayout';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { printMixins } from 'dashboard/lib/printMixins';
import { CaseLayout, CaseLayoutTab, CaseModule } from 'dashboard/models/case-template';
import { CaseModuleBase } from 'dashboard/modules/CaseModuleBase/CaseModuleBase';
import { showMessage } from 'dashboard/redux/actions/messages';
import { CaseApi } from 'dashboard/views/DashboardCaseShowView/DashboardCaseShowView';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Stack from 'design-system/components/Stack/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import download, { downloadFiles } from 'lib/download';

const PRINT_HEADER_HEIGHT = '100px';

const PrintWrapper = styled.div`
  /*
    we set a consistent width so modules have their heights calculated at an aspect ratio
    similar to standard paper sizes.
  */
  @media screen {
    width: 1200px;
    margin: 0 auto;
  }
`;

const PrintHeaderCard = styled(Card).attrs(() => ({
  elevated: true,
}))`
  top: 0;
  height: ${PRINT_HEADER_HEIGHT};
  border-radius: 0;
  width: 100vw;
  position: fixed;
  /* inquiry photo and sticker have z-index values that we need to be greater than */
  z-index: 100;

  ${printMixins.hide};
`;

const PrintHeaderSpacer = styled.div`
  height: ${PRINT_HEADER_HEIGHT};

  ${printMixins.hide};
`;

const PrintHeaderBody = styled(Card.Body)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 1200px;
  margin: 0 auto;
  padding-left: 0;
  padding-right: 0;
`;

// Excluded modules do not contain any data and are omitted from the print view
const EXCLUDED_PRINT_MODULES = [ModuleTypes.Button, ModuleTypes.CaseEmail, ModuleTypes.FieldUpdate];

// Singleton modules can be safely de-doped becuase they all display the same information and have no configuration.
const SINGLETON_MODULES = [
  ModuleTypes.EventTimeline,
  ModuleTypes.CaseInfo,
  ModuleTypes.CaseComments,
  ModuleTypes.Tags,
  ModuleTypes.CaseAttachments,
  ModuleTypes.Location,
  ModuleTypes.Graph,
  ModuleTypes.ListMatches,
  ModuleTypes.RelatedCases,
  ModuleTypes.AccountSimilaritySelfie,
  ModuleTypes.Sar,
];

export const processPrintModules = (tabs: CaseLayoutTab[]) => {
  let processedTabs = cloneDeep(tabs);

  const seenModules: string[] = [];
  processedTabs.forEach((tab) => {
    // sort modules by y position, then x position so modules are in a predictable order
    tab.modules = sortModules(tab.modules);

    // filter out excluded modules
    tab.modules = tab.modules.filter((module: CaseModule) => {
      return !EXCLUDED_PRINT_MODULES.includes(module.type);
    });

    // filter out duplicate modules
    tab.modules = tab.modules.filter((module: CaseModule) => {
      if (SINGLETON_MODULES.includes(module.type)) {
        if (seenModules.includes(module.type)) {
          return false;
        } else {
          seenModules.push(module.type);
          return true;
        }
      }

      return true;
    });

    // force all modules to be full width and dynamic heights enabled when printing, our layout algorithms will handle collision resolution
    tab.modules = tab.modules.map((module: CaseModule) => {
      return {
        ...module,
        'module-layout': {
          ...module['module-layout'],
          'dynamic-height': true,
        },
        position: {
          ...module.position,
          width: 12,
          x: 0,
        },
      };
    });
  });

  // filter out tabs with no modules
  processedTabs = processedTabs.filter((tab) => tab.modules.length > 0);

  return processedTabs;
};

export interface CaseDetailsPrintProps {
  dataApi: CaseApi;
  configuration: CaseLayout;
  runCodeWithContext: (code: string) => Promise<unknown>;
  runJsonLogicWithContext: (rules: RulesLogic) => Promise<boolean>;
}

/**
 * CaseDetailsPrint provides an alternate view of the case details page that is optimized for printing.
 * It runs any frontend custom-code that has been defined for a module and renders the modules in a single column layout.
 */
export const CaseDetailsPrint = ({
  dataApi,
  configuration,
  runCodeWithContext,
  runJsonLogicWithContext,
}: CaseDetailsPrintProps) => {
  // NOTE(brandon): since we dedupe across all tabs, we process all modules at once
  const printTabs = processPrintModules(configuration.tabs);

  const tabs: ReactNode[] = useMemo(() => {
    const caseTabs: ReactNode[] = [];
    printTabs.forEach((tab) => {
      const tabContents = (
        <PrintTab
          modules={tab.modules}
          dataApi={dataApi}
          runCodeWithContext={runCodeWithContext}
          runJsonLogicWithContext={runJsonLogicWithContext}
        />
      );

      caseTabs.push(tabContents);
    });

    return caseTabs;
  }, [printTabs, dataApi, runCodeWithContext, runJsonLogicWithContext]);

  const attachments = dataApi.case.attributes['case-attachments'];
  const downloadAttachments = async () => {
    try {
      if (attachments.length === 1) {
        await download(attachments[0]);
      } else {
        await downloadFiles(attachments, `${dataApi.case.attributes.name} attachments.zip`);
      }
    } catch {
      showMessage('Failed to download attachments. Please try again.', MessageType.Failure);
    }
  };

  return (
    <>
      <PrintHeaderSpacer />
      <PrintHeaderCard>
        <PrintHeaderBody>
          <Paragraph color='primary'>
            This document may contain sensitive information, please review before printing.
          </Paragraph>
          <Stack direction='horizontal'>
            <Button
              id='case-details-print-button__download_attachments'
              variant='basic'
              width='button'
              disabled={attachments.length === 0}
              onClick={downloadAttachments}
            >
              Download attachments ({attachments.length})
            </Button>
            <Button id='case-details-print-button__print' width='button' onClick={window.print}>
              Print
            </Button>
          </Stack>
        </PrintHeaderBody>
      </PrintHeaderCard>
      <PrintWrapper>
        {tabs.map((tabContent, index) => (
          <>
            <Spacer height={3} />
            <Heading size='lg'>{printTabs[index].name}</Heading>
            <Spacer height={3} />
            {tabContent}
          </>
        ))}
      </PrintWrapper>
    </>
  );
};

interface PrintTabProps {
  modules: CaseModule[];
  dataApi: CaseApi;
  runCodeWithContext: (code: string) => Promise<unknown>;
  runJsonLogicWithContext: (rules: RulesLogic) => Promise<boolean>;
}

const PrintTab = ({
  modules,
  dataApi,
  runCodeWithContext,
  runJsonLogicWithContext,
}: PrintTabProps) => {
  const { computedModules } = useModuleLayout(modules, runCodeWithContext, runJsonLogicWithContext);

  return (
    <>
      {computedModules.map((module) => (
        <CaseModuleBase
          key={module.token}
          module={module}
          caseApi={dataApi}
          runCodeWithContext={runCodeWithContext}
          isPrint
        />
      ))}
    </>
  );
};
